import { useState } from "react";
import { Cpu, MonitorPlay, MemoryStick, Gauge, CheckCircle2, AlertTriangle, XCircle, Wifi, Loader2, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Best-effort local hardware capability probe for 4K "Ascend Ultra" readiness.
// Everything runs in the browser — no data leaves the device.
function probeHardware() {
  const result = { webgl: null, resolution: null, cpu: null, ram: null, network: null };

  // WebGL / GPU
  try {
    const canvas = document.createElement("canvas");
    const gl2 = canvas.getContext("webgl2");
    const gl = gl2 || canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl) {
      let renderer = "GPU detected";
      const dbg = gl.getExtension("WEBGL_debug_renderer_info");
      if (dbg) renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || renderer;
      result.webgl = { supported: true, webgl2: !!gl2, renderer };
    } else {
      result.webgl = { supported: false, webgl2: false, renderer: "No WebGL" };
    }
  } catch (e) {
    result.webgl = { supported: false, webgl2: false, renderer: "No WebGL" };
  }

  // Screen resolution (physical pixels)
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round((window.screen?.width || 0) * dpr);
  const h = Math.round((window.screen?.height || 0) * dpr);
  const is4k = w >= 3840 || h >= 2160;
  const is1080 = w >= 1920 || h >= 1080;
  result.resolution = { w, h, dpr, is4k, is1080 };

  // CPU logical cores
  const cores = navigator.hardwareConcurrency || 0;
  result.cpu = { cores };

  // RAM (Chrome/Edge only; approximate, capped at 8 by the browser)
  const mem = typeof navigator.deviceMemory === "number" ? navigator.deviceMemory : null;
  result.ram = { gb: mem };

  // Network (best-effort)
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  result.network = conn ? { downlink: conn.downlink || null, type: conn.effectiveType || null } : null;

  return result;
}

function verdictFor(p) {
  const webgl2 = p.webgl?.webgl2;
  const cores = p.cpu?.cores || 0;
  const ram = p.ram?.gb; // may be null
  const ready4k = webgl2 && cores >= 8 && (ram === null || ram >= 8);
  const ready1080 = (p.webgl?.supported) && cores >= 4;
  return { ready4k, ready1080 };
}

const StatusIcon = ({ level }) =>
  level === "pass" ? <CheckCircle2 className="h-5 w-5 text-emerald-400" />
  : level === "warn" ? <AlertTriangle className="h-5 w-5 text-amber-400" />
  : <XCircle className="h-5 w-5 text-red-400" />;

function Row({ icon: Icon, label, value, level }) {
  return (
    <div data-testid={`hw-row-${label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-3 rounded-xl border border-gray-800 bg-gray-950/60 px-4 py-3">
      <Icon className="h-5 w-5 text-amber-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-gray-400 truncate">{value}</p>
      </div>
      <StatusIcon level={level} />
    </div>
  );
}

export function HardwareCheck() {
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [probe, setProbe] = useState(null);

  const run = () => {
    setOpen(true);
    setScanning(true);
    setProbe(null);
    // brief delay so the scanning state is visible / feels like a real test
    setTimeout(() => {
      setProbe(probeHardware());
      setScanning(false);
    }, 700);
  };

  const v = probe ? verdictFor(probe) : null;

  return (
    <>
      <button
        data-testid="test-hardware-button"
        onClick={run}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-all active:scale-[0.98] w-max"
      >
        <Gauge className="h-4 w-4" /> Test My Hardware
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <Gauge className="h-5 w-5 text-amber-400" /> Hardware readiness check
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              We check your browser and device for <span className="text-amber-300 font-medium">Ascend Ultra</span> 4K streaming. Nothing leaves your computer.
            </DialogDescription>
          </DialogHeader>

          {scanning || !probe ? (
            <div data-testid="hw-scanning" className="py-10 flex flex-col items-center justify-center text-center">
              <Loader2 className="h-8 w-8 text-amber-400 animate-spin" />
              <p className="mt-3 text-sm text-gray-400">Scanning your system…</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Verdict banner */}
              <div
                data-testid="hw-verdict"
                className={"rounded-xl border p-4 flex items-start gap-3 " + (v.ready4k
                  ? "border-emerald-500/30 bg-emerald-500/[0.07]"
                  : v.ready1080
                    ? "border-amber-500/30 bg-amber-500/[0.07]"
                    : "border-red-500/30 bg-red-500/[0.07]")}
              >
                {v.ready4k ? <Sparkles className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" /> : v.ready1080 ? <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" /> : <XCircle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />}
                <div>
                  <p className="text-sm font-semibold text-white">
                    {v.ready4k
                      ? "Ready for Ascend Ultra 4K streaming"
                      : v.ready1080
                        ? "Ready for 1080p — 4K may be limited"
                        : "This device may struggle with high-quality streaming"}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {v.ready4k
                      ? "Your GPU, CPU and memory look great for 4K broadcasts."
                      : v.ready1080
                        ? "You can broadcast in HD reliably. For smooth 4K we recommend 8+ CPU cores and 8GB+ RAM."
                        : "Consider a more powerful machine or streaming at 720p/1080p for stability."}
                  </p>
                </div>
              </div>

              <div className="space-y-2.5">
                <Row
                  icon={MonitorPlay}
                  label="Graphics (WebGL)"
                  value={probe.webgl?.supported ? `${probe.webgl.webgl2 ? "WebGL 2" : "WebGL 1"} · ${probe.webgl.renderer}` : "Not supported"}
                  level={probe.webgl?.webgl2 ? "pass" : probe.webgl?.supported ? "warn" : "fail"}
                />
                <Row
                  icon={MonitorPlay}
                  label="Display resolution"
                  value={probe.resolution?.w ? `${probe.resolution.w} × ${probe.resolution.h}${probe.resolution.is4k ? " (4K)" : probe.resolution.is1080 ? " (1080p+)" : ""}` : "Unknown"}
                  level={probe.resolution?.is4k ? "pass" : probe.resolution?.is1080 ? "warn" : "fail"}
                />
                <Row
                  icon={Cpu}
                  label="CPU cores"
                  value={probe.cpu?.cores ? `${probe.cpu.cores} logical cores` : "Unknown"}
                  level={probe.cpu?.cores >= 8 ? "pass" : probe.cpu?.cores >= 4 ? "warn" : "fail"}
                />
                <Row
                  icon={MemoryStick}
                  label="Memory (RAM)"
                  value={probe.ram?.gb != null ? `≈ ${probe.ram.gb} GB${probe.ram.gb >= 8 ? "+" : ""}` : "Not reported by this browser"}
                  level={probe.ram?.gb == null ? "warn" : probe.ram.gb >= 8 ? "pass" : probe.ram.gb >= 4 ? "warn" : "fail"}
                />
                {probe.network && (
                  <Row
                    icon={Wifi}
                    label="Network"
                    value={`${probe.network.type || "unknown"}${probe.network.downlink ? ` · ~${probe.network.downlink} Mbps down` : ""}`}
                    level={(probe.network.downlink || 0) >= 10 ? "pass" : "warn"}
                  />
                )}
              </div>

              <div className="flex justify-end">
                <button data-testid="hw-rescan" onClick={run} className="text-sm gold-gradient text-black font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-2">
                  <Gauge className="h-4 w-4" /> Run again
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default HardwareCheck;
